import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ExpenseHistoryTable from './ExpenseHistoryTable';
import SettleDebtModal from './SettleDebtModal';
import MemberManagement from './MemberManagement';
import './HouseholdDashboard.css';

function HouseholdDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [household, setHousehold] = useState(null);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState(null);

  useEffect(() => {
    loadHouseholdData();
  }, [id]);

  const loadHouseholdData = async () => {
    try {
      setLoading(true);
      
      // Get household details
      const response = await api.get('/household');
      const households = response.data;
      const currentHousehold = households.find(h => h.id === parseInt(id));
      
      if (!currentHousehold) {
        navigate('/households');
        return;
      }
      
      setHousehold(currentHousehold);
      
      // Load summary data
      const summaryRes = await api.get(`/household/${id}/summary`);
      const summaryData = summaryRes.data;
      
      // Parse numbers from API (they might be strings)
      setSummary({
        ...summaryData,
        total_expenses: parseFloat(summaryData.total_expenses) || 0,
        total_contributions: parseFloat(summaryData.total_contributions) || 0,
        net_balance: parseFloat(summaryData.net_balance) || 0,
        member_balances: summaryData.member_balances?.map(mb => ({
          ...mb,
          contributed: parseFloat(mb.contributed) || 0,
          paid: parseFloat(mb.paid) || 0,
          balance: parseFloat(mb.balance) || 0
        })) || []
      });
      
      // Load members
      const membersRes = await api.get(`/household/${id}/members`);
      setMembers(membersRes.data);
      
      // Load balances
      const balancesRes = await api.get(`/household/${id}/balances`);
      const balancesData = balancesRes.data;
      
      // Parse balance numbers
      setBalances({
        ...balancesData,
        total_owed_to_you: parseFloat(balancesData.total_owed_to_you) || 0,
        total_you_owe: parseFloat(balancesData.total_you_owe) || 0,
        balances: balancesData.balances?.map(b => ({
          ...b,
          you_owe: parseFloat(b.you_owe) || 0,
          owes_you: parseFloat(b.owes_you) || 0,
          net: parseFloat(b.net) || 0
        })) || []
      });
      
    } catch (error) {
      console.error('Error loading household:', error);
      navigate('/households');
    } finally {
      setLoading(false);
    }
  };

  const handleSettleUp = (balance) => {
    if (balance.net === 0) {
      alert('No debt to settle!');
      return;
    }
    setSelectedBalance(balance);
    setShowSettleModal(true);
  };

  const handleSettleSuccess = () => {
    setShowSettleModal(false);
    setSelectedBalance(null);
    loadHouseholdData();
  };

  if (loading) {
    return (
      <div className="household-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading household...</p>
        </div>
      </div>
    );
  }

  if (!household) {
    return null;
  }

  return (
    <div className="household-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="household-title">
          <div className="title-icon">🏠</div>
          <div className="title-content">
            <h1>{household.name}</h1>
            <div className="household-meta">
              <span className={`role-badge ${household.role}`}>
                {household.role}
              </span>
              <span className="members-count">
                👥 {household.member_count} {household.member_count === 1 ? 'Member' : 'Members'}
              </span>
              <span className="invite-code-badge">
                Invite Code: <code>{household.invite_code}</code>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          💳 Expenses
        </button>
        <button 
          className={`tab ${activeTab === 'debts' ? 'active' : ''}`}
          onClick={() => setActiveTab('debts')}
        >
          💰 Debts & Settlements
        </button>
        <button 
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          👥 Members
        </button>
      </div>

      {/* Content Area */}
      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">🏦</div>
                <div className="stat-content">
                  <div className="stat-label">Household Wallet</div>
                  <div className="stat-value">
                    €{household.wallet_balance ? household.wallet_balance.toFixed(2) : '0.00'}
                  </div>
                  <div className="stat-sublabel">Current balance</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">💸</div>
                <div className="stat-content">
                  <div className="stat-label">Total Expenses</div>
                  <div className="stat-value">
                    €{summary ? summary.total_expenses.toFixed(2) : '0.00'}
                  </div>
                  <div className="stat-sublabel">
                    {summary ? summary.expense_count : 0} expenses this month
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <div className="stat-label">Your Debts</div>
                  <div className="stat-value" style={{color: balances && balances.total_you_owe > 0 ? '#ef4444' : '#10b981'}}>
                    {balances && balances.total_you_owe > 0 
                      ? `-€${balances.total_you_owe.toFixed(2)}`
                      : '€0.00'}
                  </div>
                  <div className="stat-sublabel">
                    {balances && balances.total_owed_to_you > 0 
                      ? `+€${balances.total_owed_to_you.toFixed(2)} owed to you`
                      : 'All settled'}
                  </div>
                </div>
              </div>
            </div>

            <div className="quick-sections">
              <div className="expense-breakdown">
                <h2>📊 How It Works</h2>
                <div className="info-grid">
                  <div className="info-card">
                    <div className="info-icon">🏦</div>
                    <div className="info-content">
                      <h3>Shared Wallet</h3>
                      <p>Pool money together. Expenses paid from here create no debts.</p>
                    </div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-icon">💳</div>
                    <div className="info-content">
                      <h3>Personal Payment</h3>
                      <p>When you pay for everyone personally, others owe you their share.</p>
                    </div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-icon">💰</div>
                    <div className="info-content">
                      <h3>Settle Debts</h3>
                      <p>Record payments to clear debts. See the Debts tab for details.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="expenses-tab">
            <div className="expenses-header">
              <h2>💳 Household Expenses</h2>
              <p className="expenses-description">
                View all household expenses with full amounts. These are the total costs, not individual splits.
                For your personal share, check the Transaction History page.
              </p>
            </div>
            <ExpenseHistoryTable householdId={parseInt(id)} />
          </div>
        )}

        {activeTab === 'debts' && (
          <div className="debts-tab">
            <h2>💰 Debt Balances</h2>
            <p className="debt-explanation">
              💡 Debts are created when someone pays for everyone from their personal wallet.
              Expenses paid from the shared household wallet create no debts.
            </p>
            {balances && balances.balances && balances.balances.length > 0 ? (
              <div className="debts-list">
                {balances.balances.map(balance => (
                  <div key={balance.other_user_id} className="debt-card">
                    <div className="debt-header">
                      <h3>{balance.other_user_name}</h3>
                      {balance.net !== 0 && (
                        <button 
                          className="settle-btn"
                          onClick={() => handleSettleUp(balance)}
                        >
                          💳 Settle Up
                        </button>
                      )}
                    </div>
                    <div className="debt-details">
                      {balance.net > 0 ? (
                        <div className="debt-amount positive">
                          <span className="label">Owes you:</span>
                          <span className="amount">€{balance.net.toFixed(2)}</span>
                        </div>
                      ) : balance.net < 0 ? (
                        <div className="debt-amount negative">
                          <span className="label">You owe:</span>
                          <span className="amount">€{Math.abs(balance.net).toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="debt-amount settled">
                          <span className="label">All settled!</span>
                          <span className="amount">€0.00</span>
                        </div>
                      )}
                      <div className="debt-breakdown">
                        <span>They owe you: €{balance.owes_you.toFixed(2)}</span>
                        <span>You owe them: €{balance.you_owe.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="debts-summary">
                  <div className="summary-item">
                    <span>Total owed to you:</span>
                    <span className="positive">€{balances.total_owed_to_you.toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span>Total you owe:</span>
                    <span className="negative">€{balances.total_you_owe.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>🎉 No outstanding debts!</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && (
          <div className="members-tab">
            <MemberManagement 
              householdId={parseInt(id)} 
              isOwner={household.role === 'owner' || household.role === 'admin'}
              onMemberChange={loadHouseholdData}
            />
          </div>
        )}
      </div>

      {/* Settle Debt Modal */}
      {showSettleModal && selectedBalance && (
        <SettleDebtModal
          householdId={parseInt(id)}
          balance={selectedBalance}
          onClose={() => {
            setShowSettleModal(false);
            setSelectedBalance(null);
          }}
          onSuccess={handleSettleSuccess}
        />
      )}
    </div>
  );
}

export default HouseholdDashboard;
